import { QueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { HoofdstukSchema } from '@/api';
import {
  ensureQueryDataHoofdstukkenWithEdits,
  useHoofdstukkenWithEdits,
  useHoofdstukkenWithOverweging,
} from '@/api/queries/objects';
import { GRONDSLAGEN, Grondslag } from '@/constants/grondslagen';

export type BesluitGrondslag = {
  data: Grondslag;
  hoofdstukken: BesluitHoofdstuk[];
};

export type BesluitHoofdstuk = {
  data: HoofdstukSchema;
  grondslag: BesluitGrondslag;
  hasVoorschriften: boolean;
  hasOverweging: boolean;
};

/**
 * Returns lists of gronslagen with hoofdstukken that contain edits of have an inhoudelijke overweging.
 */
export const useViewerBesluitHoofdstukken = ({
  besluitId,
}: {
  besluitId: string;
}) => {
  const voorschriftHoofdstukken = useHoofdstukkenWithEdits({ besluitId });
  const hoofdstukkenWithOverwegingen = useHoofdstukkenWithOverweging({
    besluitId,
  });

  const grondslagen = useMemo(() => {
    const allHoofdstukken = mergeHoofdstukken({
      voorschriftHoofdstukken: voorschriftHoofdstukken.data,
      hoofdstukkenWithOverwegingen:
        hoofdstukkenWithOverwegingen.data?.objects ?? [],
    });

    /**
     * Group hoofdstukken by grondslag
     */
    const grondslagen = groupHoofdstukkenByGrondslag({
      allHoofdstukken,
      voorschriftHoofdstukken: voorschriftHoofdstukken.data,
      hoofdstukkenWithOverwegingen:
        hoofdstukkenWithOverwegingen.data?.objects ?? [],
    });

    return grondslagen;
  }, [voorschriftHoofdstukken.data, hoofdstukkenWithOverwegingen.data]);

  return {
    grondslagen,
    isLoading:
      voorschriftHoofdstukken.isLoading ||
      hoofdstukkenWithOverwegingen.isLoading,
  };
};

export const ensureQueryDataViewerBesluitHoofdstukken = async ({
  besluitId,
  queryClient,
}: {
  besluitId: string;
  queryClient: QueryClient;
}) => {
  const [voorschriftHoofdstukken, hoofdstukkenWithOverwegingen] =
    await Promise.all([
      ensureQueryDataHoofdstukkenWithEdits({ besluitId }, {}, queryClient),
      queryClient.ensureQueryData(
        useHoofdstukkenWithOverweging.getOptions({ besluitId })
      ),
    ]);

  const allHoofdstukken = mergeHoofdstukken({
    voorschriftHoofdstukken: voorschriftHoofdstukken ?? [],
    hoofdstukkenWithOverwegingen: hoofdstukkenWithOverwegingen.objects ?? [],
  });

  return groupHoofdstukkenByGrondslag({
    allHoofdstukken,
    voorschriftHoofdstukken,
    hoofdstukkenWithOverwegingen: hoofdstukkenWithOverwegingen.objects ?? [],
  });
};

/**
 *
 * UTILS
 *
 */

/**
 * Merges a list of voorschriftHoofdstukken and hoofdstukkenWithOverwegingen into a single list of hoofdstukken.
 */
const mergeHoofdstukken = ({
  voorschriftHoofdstukken,
  hoofdstukkenWithOverwegingen,
}: {
  voorschriftHoofdstukken: HoofdstukSchema[];
  hoofdstukkenWithOverwegingen: HoofdstukSchema[];
}) => {
  const allHoofdstukken: HoofdstukSchema[] = [...voorschriftHoofdstukken];

  /**
   * We fetch the last hoofdstuk in a lineage that has an overweging.
   * Because of this, we need to be careful that we don't overwrite a voorschrift hoofdstuk with another one in the same lineage that has an overweging.
   */
  hoofdstukkenWithOverwegingen.forEach((hoofdstukWithOverweging) => {
    if (
      !allHoofdstukken.some(
        (hoofdstuk) => hoofdstuk.Lineage === hoofdstukWithOverweging.Lineage
      )
    ) {
      allHoofdstukken.push(hoofdstukWithOverweging);
    }
  });

  allHoofdstukken.sort((a, b) => a.Nummer - b.Nummer);

  return allHoofdstukken;
};

const groupHoofdstukkenByGrondslag = ({
  voorschriftHoofdstukken,
  hoofdstukkenWithOverwegingen,
  allHoofdstukken,
}: {
  voorschriftHoofdstukken: HoofdstukSchema[];
  hoofdstukkenWithOverwegingen: HoofdstukSchema[];
  allHoofdstukken: HoofdstukSchema[];
}) => {
  const voorschriftHoofdstukkenLineageIds = voorschriftHoofdstukken.map(
    ({ Lineage }) => Lineage
  );
  const hoofdstukkenWithOverwegingenLineageIds =
    hoofdstukkenWithOverwegingen.map(({ Lineage }) => Lineage);

  return GRONDSLAGEN.map((grondslag) => {
    const groupedGrondslag: BesluitGrondslag = {
      data: grondslag,
      hoofdstukken: [],
    };

    groupedGrondslag.hoofdstukken = allHoofdstukken
      .filter(({ Grondslag }) => Grondslag === grondslag.id)
      .map((hoofdstuk) => ({
        data: hoofdstuk,
        hasOverweging: hoofdstukkenWithOverwegingenLineageIds.includes(
          hoofdstuk.Lineage
        ),
        hasVoorschriften: voorschriftHoofdstukkenLineageIds.includes(
          hoofdstuk.Lineage
        ),
        grondslag: groupedGrondslag,
      }));

    if (groupedGrondslag.hoofdstukken.length === 0) {
      return null;
    }

    return groupedGrondslag;
  }).filter((group): group is BesluitGrondslag => Boolean(group));
};
